import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { SystemIcon } from '@monorepo/nova-icons';
import { Dialog } from '@monorepo/nova-ui';
import { useSupportedSymbolsData } from '../../hooks/api';
import {
  SupportedSymbolsEmpty,
  SupportedSymbolsError,
  SupportedSymbolsList,
  SupportedSymbolsLoading,
  SupportedSymbolsSearch,
} from './components';
import { useSupportedSymbolsSearch } from './hooks';
import {
  StyledCloseButton,
  StyledContentArea,
  StyledHeaderRow,
  StyledModalBody,
  StyledNoResults,
  StyledTitle,
} from './styled';

interface SupportedSymbolsModalProps {
  open: boolean;
  onClose: () => void;
}

export const SupportedSymbolsModal: FC<SupportedSymbolsModalProps> = ({
  open,
  onClose,
}) => {
  const { t } = useTranslation(['keyMetrics']);
  const {
    data: symbols,
    isLoading,
    error,
    refetch,
  } = useSupportedSymbolsData(open);

  const { searchTerm, filteredSymbols, hasNoResults, handleSearchChange } =
    useSupportedSymbolsSearch(symbols ?? []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      variant="bare"
      aria-labelledby="supported-symbols-modal-title"
    >
      <StyledModalBody>
        <StyledHeaderRow>
          <StyledTitle variant="h3" id="supported-symbols-modal-title">
            {t('keyMetrics:supportedSymbols.title')}
          </StyledTitle>
          <StyledCloseButton aria-label="Close" onClick={onClose}>
            <SystemIcon iconName="X" color="primary" />
          </StyledCloseButton>
        </StyledHeaderRow>
        {isLoading ? (
          <SupportedSymbolsLoading />
        ) : error ? (
          <SupportedSymbolsError onRetry={refetch} />
        ) : !symbols || symbols.length === 0 ? (
          <SupportedSymbolsEmpty />
        ) : (
          <>
            <SupportedSymbolsSearch
              value={searchTerm}
              onChange={handleSearchChange}
            />
            <StyledContentArea>
              {hasNoResults ? (
                <StyledNoResults variant="body1">
                  {t('keyMetrics:supportedSymbols.noResults', {
                    query: searchTerm.trim(),
                  })}
                </StyledNoResults>
              ) : (
                <SupportedSymbolsList symbols={filteredSymbols} />
              )}
            </StyledContentArea>
          </>
        )}
      </StyledModalBody>
    </Dialog>
  );
};
